import {Injectable} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart, OrderEntry} from '@spartacus/cart/base/root';
import {CxEvent} from '@spartacus/core';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, first, map, switchMap} from 'rxjs/operators';
import {DcpActiveCartService} from '../../dcp-active-cart.service';
import {DcpDataService} from '../dcp-data.service';
import {EddlCartViewEvent} from './events/eddl-cart-view-event';
import {EddlCheckoutStartEvent} from './events/eddl-checkout-start-event';
import {EddlOrderCompleteEvent} from './events/eddl-order-complete-event';
import {
  EddlAdobeTrackingData,
  EddlAdobeTrackingDataProductData,
  EddlCollectionState
} from './models/eddl-adobe-tracking-data';
import {EddlAfterInitializationEvent} from '@app/services/analytics/EDDL/events/eddl-after-initialization-event';
import {EddlMergedAbstract} from '@app/services/analytics/EDDL/events/eddl-merged.abstract';

const EVENT_NAME_VIEWCART: string = 'cartView';
const EVENT_NAME_CHECKOUT_START: string = 'checkoutStart';
const EVENT_NAME_ORDER_COMPLETE: string = 'orderComplete';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpEddlECommerceDataService extends DcpDataService<EddlAdobeTrackingData> {
  private hasBeenInitializedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    protected activeCartService: DcpActiveCartService,
  ) {
    super();
  }

  fillData(
    event: EddlCartViewEvent | EddlCheckoutStartEvent | EddlOrderCompleteEvent,
    activeCart?: Cart
  ): EddlAdobeTrackingData {
    const data: EddlAdobeTrackingData = {};
    if (activeCart) {
      const trackingData = {
        cart: {
          currency: activeCart.totalPrice?.currencyIso,
          id: activeCart.guid,
        },
        products: this.getRelevantProducts(activeCart.entries)
      };

      if (event instanceof EddlCartViewEvent) {
        data.event = EVENT_NAME_VIEWCART;
        data.ecommerce = trackingData;
      }
      if (event instanceof EddlCheckoutStartEvent) {
        data.event = EVENT_NAME_CHECKOUT_START;
        data.ecommerce = trackingData;
      }
      if (event instanceof EddlOrderCompleteEvent) {
        data.event = EVENT_NAME_ORDER_COMPLETE;
        data.ecommerce = trackingData;
        data.ecommerce.cart.orderID = activeCart.code;
      }
    }
    return data;
  }

  getData(event: CxEvent): Observable<EddlAdobeTrackingData> | EddlAdobeTrackingData {
    if (event instanceof EddlAfterInitializationEvent) {
      this.hasBeenInitializedSubject.next(true);
    }

    if (!(event instanceof EddlCartViewEvent || event instanceof EddlCheckoutStartEvent || event instanceof EddlOrderCompleteEvent)) {
      return undefined;
    }

    return this.hasBeenInitializedSubject.pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter(Boolean),
      first(),
      switchMap(() =>
        combineLatest([
          this.activeCartService.getActiveCart()
        ]).pipe(
          first(),
          untilDestroyed(this),
          map(([activeCart]) => {
            if (event instanceof EddlMergedAbstract) {
              event.collectState = EddlCollectionState.FINISH;
              event.collectToken = activeCart.guid;
            }
            return this.fillData(event, activeCart);
          }),
        )
      )
    );
  }

  public getRelevantProducts(entries: OrderEntry[]): EddlAdobeTrackingDataProductData[] {
    return entries.reduce((accumulator: EddlAdobeTrackingDataProductData[], entry: OrderEntry) => {
      if (!entry.product || !entry.totalPrice || Object.keys(entry.product).includes('configuratorType')) {
        return accumulator;
      }
      const product: EddlAdobeTrackingDataProductData = {
        id: entry.product.supplierAlternativeAID,
        name: entry.product.name,
        quantity: entry.quantity.toString(),
        price: entry.totalPrice.value.toString(),
      };
      if (entry.product.categories && entry.product.categories[0]?.name) {
        product.category = entry.product.categories[0].name;
      }
      if (entry.product.vendor?.name) {
        product.partnerName = entry.product.vendor.name;
      }
      return [...accumulator, product];
    }, []);
  }
}
